/**
 * Placeholder copy for the canvases: what to type, with one example.
 *
 * Examples name real measures only for a domain we know the vocabulary of.
 * For any other model they stay generic, because an example naming a measure
 * the selected model does not have teaches the user to ask something that will
 * fail.
 */
import type { DocumentSectionKind } from "../../services/api";
import type { DataDomain } from "../../services/dataDomain";
import type { BoxKind } from "./canvasModel";

export interface CanvasPrompt {
  /** What kind of thing belongs here, in business terms. */
  label: string;
  /** One example, phrased the way a user would type it. */
  example: string;
}

type Examples = { finance: string; generic: string };

function pick(examples: Examples, domain: DataDomain | null | undefined): string {
  return `e.g. “${domain === "corporate_finance" ? examples.finance : examples.generic}”`;
}

/** Shown under the dashboard grid rather than repeated in every box. */
export const DASHBOARD_TIP =
  "Tip: choose what each box shows, then describe it. The same kind can be used in more than one box.";

/** The box's chosen kind supplies the chart, so the examples do not name one. */
export const DASHBOARD_BOX_KINDS: Record<BoxKind, { label: string } & Examples> = {
  headline: {
    label: "A headline number",
    finance: "Total revenue this quarter",
    generic: "Your most important number this month",
  },
  trend: {
    label: "A trend over time",
    finance: "Monthly operating expenses for the last 12 months",
    generic: "How a key measure changed month by month",
  },
  comparison: {
    label: "A comparison",
    finance: "Revenue by business unit",
    generic: "A key measure compared across teams or regions",
  },
  breakdown: {
    label: "A breakdown or list",
    finance: "Top 10 cost centres by spend",
    generic: "The top 10 items by a key measure",
  },
  auto: {
    label: "Let the AI choose",
    finance: "Operating expenses versus budget by quarter",
    generic: "Describe what you want to see",
  },
};

export function dashboardBoxPrompt(
  kind: BoxKind,
  domain: DataDomain | null | undefined,
): CanvasPrompt {
  const entry = DASHBOARD_BOX_KINDS[kind];
  return { label: entry.label, example: pick(entry, domain) };
}

export interface DocumentSectionCopy {
  label: string;
  /** One line under the label saying what the section will contain. */
  description: string;
}

export const DOCUMENT_SECTIONS: Record<DocumentSectionKind, DocumentSectionCopy & Examples> = {
  overview: {
    label: "Overview",
    description: "What the document covers and who it is for. Written for you.",
    finance: "Quarterly finance review for the leadership team, focused on revenue and cost",
    generic: "Monthly performance review for the leadership team",
  },
  headline_number: {
    label: "Headline number",
    description: "A single figure readers should see first.",
    finance: "Total revenue this quarter",
    generic: "Your most important number this month",
  },
  chart: {
    label: "Chart",
    description: "A trend, comparison or share.",
    finance: "Monthly operating expenses for the last 12 months",
    generic: "How a key measure changed month by month",
  },
  table: {
    label: "Table",
    description: "Rows of detail, such as a ranking or a list of exceptions.",
    finance: "Top 10 cost centres by spend",
    generic: "The top 10 items by a key measure",
  },
  key_insights: {
    label: "Key insights",
    description: "What the figures in this document show. Written for you.",
    finance: "What changed most versus last quarter, and why it matters",
    generic: "What stands out in the figures above",
  },
  summary: {
    label: "Summary",
    description: "The conclusion and what to do next. Written for you.",
    finance: "Close with the three things leadership should act on",
    generic: "Close with the main takeaway and next steps",
  },
};

export function documentSectionPrompt(
  kind: DocumentSectionKind,
  domain: DataDomain | null | undefined,
): CanvasPrompt {
  const entry = DOCUMENT_SECTIONS[kind];
  return { label: entry.label, example: pick(entry, domain) };
}

export const DOCUMENT_TIP =
  "Tip: data sections are answered from the semantic model; written sections are composed from those answers.";
